import type { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { applyStatusGate } from '../statusGate';
import type {
  AnalyticalDatasetUsageRef,
  CohortDefinitionStoreActions,
  CohortDefinitionStoreState,
  CohortProfileState,
  StudyPeriodState,
} from '../types';

type Store = CohortDefinitionStoreState & CohortDefinitionStoreActions;
type SetState = (
  partial: Store | Partial<Store> | ((state: Store) => Store | Partial<Store>)
) => void;

// isSaving safety timeout — auto-resets if socket ack never arrives
const SAVING_TIMEOUT_MS = 10_000;
let savingTimeoutId: ReturnType<typeof setTimeout> | undefined;

export const initialStudyPeriod: StudyPeriodState = {
  overallStudyPeriodRestriction: undefined,
  changeStatus: undefined,
  changedBy: undefined,
};

export const initialProfile: CohortProfileState = {
  latestStatus: undefined,
  latestCohortVersionId: undefined,
  isEditable: false,
  isSaving: false,
  isExecutionPending: false,
  hasServerConfirmedProcessing: false,
  latestSubjectCount: undefined,
  latestDatasetReleaseId: undefined,
  latestDatasetReleaseDisplayName: undefined,
  usedInAnalyticalDatasets: [],
};

export const createProfileSlice = (set: SetState) => ({
  studyPeriod: initialStudyPeriod,
  profile: initialProfile,
  // TODO: cleanup with asset_edit_while_in_use ff — top-level (not on `profile`)
  // so profile-shape assertions and `setProfileFromServer` are untouched.
  assetEditWhileInUseEnabled: false,
  // TODO: cleanup with skip_process_confirmation_modal ff
  skipProcessConfirmationModalEnabled: false,

  setStudyPeriod: (studyPeriod: StudyPeriodState) => set({ studyPeriod }),

  setUsedInAnalyticalDatasets: (refs: AnalyticalDatasetUsageRef[]) =>
    set((state) => ({ profile: { ...state.profile, usedInAnalyticalDatasets: refs } })),

  // TODO: cleanup with asset_edit_while_in_use ff
  setAssetEditWhileInUseEnabled: (enabled: boolean) => set({ assetEditWhileInUseEnabled: enabled }),

  // TODO: cleanup with skip_process_confirmation_modal ff
  setSkipProcessConfirmationModalEnabled: (enabled: boolean) =>
    set({ skipProcessConfirmationModalEnabled: enabled }),

  setProfileFromServer: (
    profile: Omit<CohortProfileState, 'usedInAnalyticalDatasets' | 'hasServerConfirmedProcessing'>
  ) =>
    set((state) => {
      if (profile.latestStatus) {
        const gate = applyStatusGate(
          state.profile.isExecutionPending,
          state.profile.hasServerConfirmedProcessing,
          profile.latestStatus,
          state.skipProcessConfirmationModalEnabled
        );
        if (!gate.accept) {
          // Accept all fields except latestStatus and isExecutionPending
          const { latestStatus: _rejected, isExecutionPending: _keep, ...rest } = profile;
          return { profile: { ...state.profile, ...rest } };
        }
        return {
          profile: {
            // Merge over existing so fields sourced elsewhere (e.g.
            // usedInAnalyticalDatasets) survive a profile refresh.
            ...state.profile,
            ...profile,
            isExecutionPending: gate.clearGate ? false : state.profile.isExecutionPending,
            hasServerConfirmedProcessing:
              state.profile.hasServerConfirmedProcessing ||
              profile.latestStatus === ('PROCESSING' as CohortDefinitionStatus),
          },
        };
      }
      // No status → accept everything but preserve isExecutionPending
      const { isExecutionPending: _keep, ...rest } = profile;
      return { profile: { ...state.profile, ...rest } };
    }),

  setProfileStatusFromServer: (status: CohortDefinitionStatus) =>
    set((state) => {
      const gate = applyStatusGate(
        state.profile.isExecutionPending,
        state.profile.hasServerConfirmedProcessing,
        status,
        state.skipProcessConfirmationModalEnabled
      );
      if (!gate.accept) return state;
      return {
        profile: {
          ...state.profile,
          latestStatus: status,
          isExecutionPending: gate.clearGate ? false : state.profile.isExecutionPending,
          hasServerConfirmedProcessing:
            state.profile.hasServerConfirmedProcessing ||
            status === ('PROCESSING' as CohortDefinitionStatus),
        },
      };
    }),

  setProfileStatus: (status: CohortDefinitionStatus) =>
    set((state) => ({ profile: { ...state.profile, latestStatus: status } })),

  setExecutionPending: (pending: boolean) =>
    set((state) => ({
      profile: {
        ...state.profile,
        isExecutionPending: pending,
        // A new run starting: the previous run's server-confirmed PROCESSING
        // (if any) no longer applies to whatever comes next.
        ...(pending ? { hasServerConfirmedProcessing: false } : {}),
      },
    })),

  setIsSaving: (isSaving: boolean) => {
    clearTimeout(savingTimeoutId);
    if (isSaving) {
      savingTimeoutId = setTimeout(() => {
        set((state) => ({ profile: { ...state.profile, isSaving: false } }));
      }, SAVING_TIMEOUT_MS);
    }
    set((state) => ({ profile: { ...state.profile, isSaving } }));
  },

  resetOnSocketEvent: ({
    newStatus,
    clearStagedChanges = true,
  }: {
    newStatus?: CohortDefinitionStatus;
    clearStagedChanges?: boolean;
  }) => {
    clearTimeout(savingTimeoutId);
    set((state) => {
      // During execution: apply the status gate to prevent stale socket events
      // (e.g. staged_changes_updated with DRAFT) from reverting the optimistic PROCESSING status.
      if (state.profile.isExecutionPending) {
        const gate = newStatus
          ? applyStatusGate(
              true,
              state.profile.hasServerConfirmedProcessing,
              newStatus as CohortDefinitionStatus,
              state.skipProcessConfirmationModalEnabled
            )
          : { accept: false, clearGate: false };

        return {
          ...(clearStagedChanges ? { hasStagedChanges: false } : {}),
          profile: {
            ...state.profile,
            isSaving: false,
            ...(gate.accept ? { latestStatus: newStatus as CohortDefinitionStatus } : {}),
            ...(gate.clearGate ? { isExecutionPending: false } : {}),
            ...(gate.accept && newStatus === ('PROCESSING' as CohortDefinitionStatus)
              ? { hasServerConfirmedProcessing: true }
              : {}),
          },
        };
      }

      return {
        ...(clearStagedChanges ? { hasStagedChanges: false } : {}),
        profile: {
          ...state.profile,
          isExecutionPending: false,
          isSaving: false,
          ...(newStatus ? { latestStatus: newStatus as CohortDefinitionStatus } : {}),
        },
      };
    });
  },
});
